import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DeleteIcon } from '../../assets/icons/delete-icon.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import { ReactComponent as HistoryIcon } from '../../assets/icons/history-icon.svg';
import EditGuestCharging from '../../pages/guest-charging/components/EditGuestCharging';
import GuestChargingHistory from '../../pages/guest-charging/components/GuestChargingHistory';
import GuestChargingViewModal from '../../pages/guest-charging/components/GuestChargingView';
import { useAppDispatch } from '../../redux/hooks';
import { fetchGuestChargingHistory } from '../../redux/slice/GuestChargingSlice';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import guestChargingApi from '../../utils/api/guest-charging';
import DeleteWarning from '../delete-warning/DeleteWarning';
import ModalBox from '../modal-box/ModalBox';
import SuccessNotification from '../success-notification/SuccessNotification';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
  header: boolean;
  closeModal: () => void;
}

/**
 * Guest Charging Side Panel
 * @returns - JSX of Guest Charging Side Panel
 */
function GuestChargingSidePanel(properties: {
  selectedGuest: string;
  onDeleteSuccess?: () => void; // Added prop for deletion callback
  currentPage: 'view' | 'edit' | 'history';
  setCurrentPage: (page: 'view' | 'edit' | 'history') => void;
}) {
  const { t } = useTranslation('guestCharging');
  const { selectedGuest, onDeleteSuccess, currentPage, setCurrentPage } = properties;
  const dispatch = useAppDispatch();
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
    header: false,
    /**
     * Close modal
     */
    closeModal: () => setModalBoxStatus('close'),
  });

  /**
   * View the guest charging
   * @param rowId
   */
  const handleHistoryOfGuestCharging = (rowId?: string | null) => {
    dispatch(fetchGuestChargingHistory({ id: rowId || '' }));
    setCurrentPage('history');
  };

  /**
   * Delete station API call
   * @param rowId
   */
  const handleDeleteGuestCharging = (rowId?: string | null) => {
    if (rowId) {
      guestChargingApi
        .deleteGuestCharging(rowId)
        .then(() => {
          setModalBoxStatus('open');
          setModalBoxDetail({
            title: '',
            content: (
              <SuccessNotification
                title={t('guestDeletedSuccess')}
                description=""
                notificationCloseTimer={3000}
                onNotificationClose={() => {
                  setModalBoxStatus('close');
                  // dispatch(updateSingleStation(response.data));
                }}
              />
            ),
            notificationModal: true,
            header: false,
            /**
             * Close modal with function to dispatch station details
             */
            closeModal: () => {
              setModalBoxStatus('close');
              // dispatch(updateSingleStation(response.data));
            },
          });
          if (onDeleteSuccess) onDeleteSuccess(); // Trigger parent refresh
        })
        .catch((error) => {
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message: error?.response?.data?.message || t('somethingWentWrong'),
              },
            }),
          );
        });
    } else {
      setModalBoxDetail({
        title: '',
        content: null,
        notificationModal: false,
        header: true,
        /**
         * Close modal
         */
        closeModal: () => setModalBoxStatus('close'),
      });
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: 200,
            message: t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  /**
   * Delete station warning popup
   */
  const handleDeleteGuestCharingWarning = (rowId?: string | null) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      content: (
        <DeleteWarning
          selectedRowId={rowId}
          title={t('title')}
          description={t('description')}
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitSvgIcon={<DeleteIcon />}
          submitButtonOnClick={handleDeleteGuestCharging}
        />
      ),
      notificationModal: true,
      header: false,
      /**
       * Close modal
       */
      closeModal: () => setModalBoxStatus('close'),
    });
  };

  /**
   * Render page content based on current page
   */
  const renderPage = () => {
    switch (currentPage) {
      case 'edit':
        return (
          <EditGuestCharging
            selectedGuestId={selectedGuest}
            onCancel={() => setCurrentPage('view')}
          />
        );

      case 'history':
        return (
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <GuestChargingHistory />
          </div>
        );

      case 'view':
      default:
        return <GuestChargingViewModal rowId={selectedGuest} />;
    }
  };

  return (
    <div className="guest-charging-side-panel">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={modalBoxDetail.closeModal}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />

      <div className="guest-charging-side-panel-header">
        {currentPage === 'view' && (
          <>
            <p className="title">{t('guestCharging')}</p>
            <div className="guest-charging-action-button">
              <EditIcon className="icon edit" onClick={() => setCurrentPage('edit')} />
              <HistoryIcon
                className="icon history"
                onClick={() => handleHistoryOfGuestCharging(selectedGuest)}
              />
              <DeleteIcon
                className="icon delete"
                onClick={() => handleDeleteGuestCharingWarning(selectedGuest)}
              />
            </div>
          </>
        )}
        {currentPage === 'edit' && (
          <>
            <p className="title">{t('editGuestCharging')}</p>
            <div className="guest-charging-action-button">
              {/* <EditIcon className="icon edit" onClick={() => setCurrentPage('view')} /> */}
            </div>
          </>
        )}
        {currentPage === 'history' && (
          <>
            <p className="title">{t('guestChargingHistory')}</p>
            <div className="guest-charging-action-button" />
          </>
        )}
      </div>

      <div className="guest-charging-side-panel-body">{renderPage()}</div>
    </div>
  );
}

export default GuestChargingSidePanel;
